import React, { useState } from 'react';
import { Document, User } from '../../../types';
import icons from '../../../layout/Icons';
import { folderService } from '../../../services/api';

interface DocumentCardProps {
  document: Document;
  currentUser?: User | null;
  onDocumentDeleted?: () => void;
  folderId: number;
}

const DocumentCard: React.FC<DocumentCardProps> = ({ document, currentUser, onDocumentDeleted, folderId }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return icons.PDF;
      case 'doc':
      case 'docx':
        return icons.Document;
      case 'xls':
      case 'xlsx':
        return icons.Excel;
      case 'ppt':
      case 'pptx':
        return icons.PowerPoint;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return icons.Image;
      case 'zip':
      case 'rar':
        return icons.Zip;
      default:
        return icons.Document;
    }
  };

  const handleDownload = () => {
    // Solo los administradores pueden descargar documentos
    if (currentUser?.role === 'employee') {
      alert('Los empleados no tienen permisos para descargar documentos');
      return;
    }
    
    const downloadUrl = folderService.downloadDocument(document.pk_document, folderId);
    window.open(downloadUrl, '_blank');
  };

  const handleView = () => {
    // Usar la URL directa del archivo en lugar de la API
    const fileUrl = document.file_url || document.file;
    
    // Debug temporal
    console.log('Document:', document);
    console.log('File URL:', fileUrl);
    
    if (!fileUrl) {
      alert('Error: No se puede abrir el documento. URL no disponible.');
      return;
    }
    
    // Abrir el documento en nueva pestaña
    let finalUrl = fileUrl;
    
    // Para employees y PDFs, agregar parámetros restrictivos
    if (currentUser?.role === 'employee') {
      const extension = document.name.split('.').pop()?.toLowerCase();
      if (extension === 'pdf') {
        finalUrl = `${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH&zoom=100`;
      }
    }
    
    console.log('Opening URL:', finalUrl);
    
    const newWindow = window.open(finalUrl, '_blank');
    
    if (newWindow) {
      newWindow.focus();
      
      // Para employees, intentar agregar restricciones adicionales
      if (currentUser?.role === 'employee') {
        newWindow.addEventListener('load', () => {
          try {
            // Deshabilitar atajos de teclado
            newWindow.document.addEventListener('keydown', (e) => {
              if (e.ctrlKey && (e.key === 's' || e.key === 'p')) {
                e.preventDefault();
                return false;
              }
            });
            
            // Deshabilitar clic derecho
            newWindow.document.addEventListener('contextmenu', (e) => {
              e.preventDefault();
              return false;
            });
          } catch (e) {
            // Ignorar errores de permisos cross-origin
            console.log('No se pudieron aplicar restricciones:', e);
          }
        });
      }
    } else {
      alert('Error: No se pudo abrir el documento. Verifica que tu navegador permita ventanas emergentes.');
    }
  };



  const handleDelete = async () => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar "${document.name}"?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await folderService.deleteDocument(document.pk_document);
      if (response.success) {
        alert('Documento eliminado exitosamente');
        onDocumentDeleted?.();
      } else {
        alert(`Error: ${response.error || 'No se pudo eliminar el documento'}`);
      }
    } catch (error) {
      console.error('Error eliminando documento:', error);
      alert('Error inesperado al eliminar el documento');
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <div 
      className="document-card" 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        cursor: currentUser?.role === 'employee' ? 'pointer' : 'default',
        marginBottom: '16px'
      }}
      onClick={currentUser?.role === 'employee' ? handleView : undefined}
      title={currentUser?.role === 'employee' ? 'Hacer clic para ver el documento' : undefined}
    >
      <div className="document-card-header">
        <div className="document-card-icon">{getFileIcon(document.name)}</div>
        <div className="document-card-actions">
          {/* Botón Ver - disponible para todos */}
          <button
            onClick={handleView}
            className="document-card-action-btn view-btn"
            title="Ver documento"
          >
            {icons.Eye}
            <span>Ver</span>
          </button>
          {/* Botón Descargar - disponible para administradores 
          {currentUser?.role !== 'employee' && (
            <button
              onClick={handleDownload}
              className="document-card-action-btn download-btn"
              title="Descargar"
            >
              {icons.Download}
              <span>Descargar</span>
            </button>
          )}
            */}
          {currentUser?.role === 'admin' && (
            <button
              onClick={handleDelete}
              className="document-card-action-btn delete-btn"
              title="Eliminar"
              disabled={isDeleting}
            >
              {icons.Delete}
              <span>{isDeleting ? 'Eliminando...' : 'Eliminar'}</span>
            </button>
          )}
        </div>
      </div>
      
      <div className="document-card-content" style={{ flex: 1 }}>
        <h3 className="document-card-title" title={document.name}>
          {document.name}
        </h3>
        <div className="document-card-stats">
          <span className="document-card-stat">
            {formatFileSize(document.file_size)}
          </span>
        </div>
        <div className="document-card-date">
          {new Date(document.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;